// src/admin/pages/shipping/shippingService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  serverTimestamp,
  query,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { ShippingCountry } from './shipping';

const COLLECTION = 'shippingRates';
const ITEMS_PER_PAGE = 10;

export interface PaginatedShippingResult {
  countries: ShippingCountry[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface ShippingStats {
  totalCountries: number;
  totalStates: number;
  countriesWithFreeShipping: number;
}

export class ShippingService {

  /* ===================== GET STATS ===================== */
  static async getStats(): Promise<ShippingStats> {
    try {
      const snapshot = await getDocs(collection(db, COLLECTION));

      let totalStates = 0;
      let countriesWithFreeShipping = 0;

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        totalStates += (data.states || []).length;
        if (data.freeShippingLimit && data.freeShippingLimit > 0) {
          countriesWithFreeShipping++;
        }
      });

      return {
        totalCountries: snapshot.size,
        totalStates,
        countriesWithFreeShipping,
      };
    } catch (error) {
      console.error('Error fetching shipping stats:', error);
      throw error;
    }
  }

  /* ===================== GET PAGINATED =====================
   *
   * FIX: Replaced cursor-based pagination (startAfter / endBefore /
   * limitToLast) with a two-step cursor-seek so any page can be fetched
   * in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
   *             seek). The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   * ========================================================= */
  static async getPaginated(
    pageSize: number = ITEMS_PER_PAGE,
    page: number = 1,
    searchQuery?: string
  ): Promise<PaginatedShippingResult> {
    try {
      const countries: ShippingCountry[] = [];
      const collectionRef = collection(db, COLLECTION);

      const orderConstraint = orderBy('createdAt', 'desc');

      // ── Total count (0 doc reads) ──────────────────────────────────────────
      const countSnapshot = await getCountFromServer(query(collectionRef));
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ───────────────────────────────────
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const country: ShippingCountry = {
          id: docSnap.id,
          name: data.name,
          code: data.code,
          defaultShippingCost: data.defaultShippingCost,
          freeShippingLimit: data.freeShippingLimit ?? 0,
          states: (data.states || []).map((s: any) => ({
            ...s,
            freeShippingLimit: s.freeShippingLimit ?? 0,
          })),
          createdBy: data.createdBy,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        };

        if (searchQuery && searchQuery.trim() !== '') {
          const searchLower = searchQuery.toLowerCase();
          const matchesCountry =
            country.name.toLowerCase().includes(searchLower) ||
            country.code.toLowerCase().includes(searchLower);
          const matchesState = country.states.some(state =>
            state.name.toLowerCase().includes(searchLower)
          );
          if (matchesCountry || matchesState) countries.push(country);
        } else {
          countries.push(country);
        }
      });

      return {
        countries,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching paginated shipping rates:', error);
      throw error;
    }
  }

  /**
   * @deprecated Use getPaginated instead
   */
  static async getAll(): Promise<ShippingCountry[]> {
    try {
      const snapshot = await getDocs(collection(db, COLLECTION));

      return snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          name: data.name,
          code: data.code,
          defaultShippingCost: data.defaultShippingCost,
          freeShippingLimit: data.freeShippingLimit ?? 0,
          states: (data.states || []).map((s: any) => ({
            ...s,
            freeShippingLimit: s.freeShippingLimit ?? 0,
          })),
          createdBy: data.createdBy,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as ShippingCountry;
      });
    } catch (error) {
      console.error('Error fetching shipping rates:', error);
      throw error;
    }
  }

  static async getById(id: string): Promise<ShippingCountry | null> {
    try {
      const snap = await getDoc(doc(db, COLLECTION, id));
      if (!snap.exists()) return null;

      const data = snap.data();
      return {
        id: snap.id,
        name: data.name,
        code: data.code,
        defaultShippingCost: data.defaultShippingCost,
        freeShippingLimit: data.freeShippingLimit ?? 0,
        states: (data.states || []).map((s: any) => ({
          ...s,
          freeShippingLimit: s.freeShippingLimit ?? 0,
        })),
        createdBy: data.createdBy,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as ShippingCountry;
    } catch (error) {
      console.error('Error fetching shipping rate:', error);
      throw error;
    }
  }

  static async create(country: ShippingCountry, userId: string): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, COLLECTION), {
        name: country.name,
        code: country.code,
        defaultShippingCost: country.defaultShippingCost,
        freeShippingLimit: country.freeShippingLimit ?? 0,
        states: country.states.map(s => ({
          ...s,
          freeShippingLimit: s.freeShippingLimit ?? 0,
        })),
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating shipping rate:', error);
      throw error;
    }
  }

  static async update(id: string, country: ShippingCountry): Promise<void> {
    try {
      await updateDoc(doc(db, COLLECTION, id), {
        name: country.name,
        code: country.code,
        defaultShippingCost: country.defaultShippingCost,
        freeShippingLimit: country.freeShippingLimit ?? 0,
        states: country.states.map(s => ({
          ...s,
          freeShippingLimit: s.freeShippingLimit ?? 0,
        })),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating shipping rate:', error);
      throw error;
    }
  }

  static async delete(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTION, id));
    } catch (error) {
      console.error('Error deleting shipping rate:', error);
      throw error;
    }
  }
}